/** @module csi/action/NewFileController */
define([
	"dojo/_base/declare",
	"dojo/Deferred",
	"src/c8-platform/service/aggregateStoreService",
	"underscore",
	"csi/action/Controller",
	"csi/comm/PostRequest",
	"csi/image/_base",
	"csi/locale",
	"csi/reflection",
	"csi/service/fileService",
	"csi/sessionConstants",
	"csi/page/localeProvider"
], function(declare, Deferred, {clearAggregateStoreResponsesByNodeType}, _, Controller, PostRequest, csiImage, locale, reflection, fileService, sessionConstants, localeProvider) {

	return declare(Controller, {
		// Boolean: determines if file upload continues even when some files are invalid
		allowPartialUpload: false,

		// Object: mapping of file names to file objects
		filesMap: null,

		// String: The name of the module that contains the operations to upload to
		moduleName: "SiteAdmin",

		// String: the name of the operation that will be uploaded to
		operationName: "MultiFilePublish",

		// Boolean: where to insert new files (set to 'true' to prepend to list, leave as 'false' to append)
		prepend: false,

		// String: type of node to create to reference file - if empty, then just create map of files
		typeNode: null,

		// Boolean: determines whether or not to not immediately send files when sending a post request from process
		sendFilesOnProcess: true,

		constructor: function() {
			// summary: try to automatically determine type of node to be created to wrap file (if any)
			const {m_idAttrParent, typeNode} = this;

			if (!typeNode) {
				const rc = reflection.getNodeClass(this.getParent());
				const rcr = rc?.getRelation(m_idAttrParent, true);
				this.typeNode = rcr?.getDestinationClass(rc)?.type;
			}
		},

		getNewFileNodes: function() {
			// summary: get nodes resulting from action for postprocessing - only valid if type specified
			return this.m_response.getResultNodes();
		},

		/**
		 * Add custom parameters to request
		 * @param {PostRequest} req
		 */
		onUploadRequest: function(req) { },

		process: function() {
			const files = this.actionValue;
			// Boolean: whether to use turntable upload operation
			let isTurntable = false;
			if (!files || !files.length) {
				this.postProcess();
				return;
			}

			const names = _.map(files, fileService.getFileName);
			// this.typeNode is not defined inside the _.each function
			const { typeNode } = this;
			let dfd = new Deferred().resolve();
			let failedFiles = new Map();

			_.each(files, function(file, i) {
				const name = names[i];
				// if new node type is image, then validate that files are images or a turntable zip file
				if (typeNode === reflection.TYPE_IMAGE) {

					if (fileService.isZipFileName(name)) {
						// file extension is valid for an zip - now check it's images
						dfd = dfd.then(function() {
							isTurntable = true;
							return fileService.validateZipIsTurntable(file, false, localeProvider).then(null, function(err) {
								failedFiles.set(locale.resource(err.message), file);
							});
						});
					} else if (csiImage.isValidFormat(name)) {
						// file extension is valid for an image - now check it's signature
						dfd = dfd.then(function() {
							return fileService.validateFile(file).then(null, function(err) {
								failedFiles.set(file.name, file);
							});
						});
					} else {
						//else add it to the list of failed files
						failedFiles.set(file.name, file);
						return;
					}
				} else {
					dfd = dfd.then(function() {
						return fileService.validateFile(file).then(null, function(err) {
							failedFiles.set(file.name, file);
						});
					});
				}
			});

			this.filesMap = new Map(files.map(element => [element.name, element]));

			this.when(dfd, () => {
				if (failedFiles.size && !this.allowPartialUpload) {
					// stop processing and display error
					if (isTurntable > 0) {
						this.displayErrorDialog({
							message: locale.resource(Array.from(failedFiles.keys())[0])
						});
					} else {
						this.displayErrorDialog({
							message: locale.resource("SRV_ERR_SiteAdmin_INVALID_FILE_TYPE", new Array(Array.from(failedFiles.keys()).join(", ")))
						});
					}
				} else if (failedFiles.size && failedFiles.size == files.length) {
					this.displayErrorDialog({
						message: locale.resource("FileUploadAllInvalidFiles")
					});
				} else {

					// Prevent invalid files from being uploaded if partial upload is allowed
					for (let failedEntry of failedFiles) {
						const failedIndex = files.indexOf(failedEntry[1]);
						names.splice(failedIndex, 1);
						files.splice(failedIndex, 1);
					}

					const filenames = this.operationName === "CreateDocumentNodes" ? names : names.join(";");

					// proceed with upload
					const req = new PostRequest(this.moduleName, this.operationName)
						.addParameter("Prepend", this.prepend)
						.addParameter("NewNodeTypes", this.getNodeTypesForFileNames(names).join(";"))
						.addParameter(this.typeNode ? "NewNodeAttrId" : "NewFileAttrId", this.m_idAttrParent)
						.addParameter("SessionURL", sessionConstants.sessionUrl)
						.addParameter("Turntable", isTurntable)
						.addParameter("URL", this.m_urlParent);

					if (this.sendFilesOnProcess) {
						req.addFiles(files, null, "FormFileFieldName");
					} else {
						req.addParameter("FileNames", filenames);
					}

					this.onUploadRequest(req);
					this.waitForPublish(req.post(), !this.allowPartialUpload);

					// Error dialog only displays if this code is called after the operation
					if (failedFiles.size) {
						this.displayErrorDialog({
							message: locale.resource("FileUploadPartialInvalidFiles"),
							onResponse: () => { }
						});
					}

				}
			});
		},

		/**
		 * @override
I        * In case there is a valid error code from the server, then use localized error message
		 * Otherwise use generic error message
		 */
		getError: function(suffix, params) {
			return this._errorCode ? this.m_error : locale.resource("FileBrowse_Error");
		},

		getNodeTypesForFileNames: function(/*String[]*/fileNames) {
			// summary: return an array of types of wrapping nodes which must be created for the each file published
			var nodeType = this.typeNode || "";

			return _.map(fileNames, function(fileName) {
				return nodeType;
			});
		},

		/**
		 * @override
		 * Clear aggregate store cache of responses that contain results of the new node type
		 */
		_onPublishResponse(response) {
			if (this.typeNode) {
				clearAggregateStoreResponsesByNodeType(this.typeNode);
			}

			this.inherited(arguments);
		},
	});
});
